/**
 * SearchResults — 강의 작업 화면의 문제 검색 결과(설계 FE-20260912 §W01·§W03 · §5).
 *
 * 이 칸이 지키는 한 가지: **결과가 어느 식에서 나왔는지 늘 보이게 한다.** 개편 전에는 조건을 바꿔도 이전 건수가
 * 지금 조건의 결과처럼 남았다(감사 보고서의 79→87 사고). 그래서 상태를 갈라 말한다 —
 *   none   검색 전. 「0문제」로 쓰지 않는다(0건과 검색 안 함은 다르다)
 *   fresh  지금 식으로 한 검색. 건수 + 검색 기준 + 시각
 *   stale  식이 바뀌었다. 아래는 「이전 검색」이고 쪽 이동을 막는다(이전 식의 다음 쪽은 지금 조건과 무관하다)
 *   읽는 중 · 실패 · 0건 · 없는 값 경고는 위 셋과 따로 겹친다.
 * 판정(freshness)은 작업 상태 훅이 한다(workspaceModel.freshness) — 이 칸은 받은 판정을 그리기만 한다.
 *
 * 검색을 스스로 쏘지 않는다. [다시 검색]·[다시 시도]·쪽 이동을 **누를 때만** 부모에게 알린다
 * (408 도 자동 재시도하지 않는다 — 5초를 넘긴 식을 다시 보내면 또 5초를 쓴다, 설계 §W03).
 *
 * 카드 본문 글자는 18px × (칸 폭 / 423) 이다(ProblemBodyPreview). 12px 아래로 떨어지지 않으려면 이 칸의 안쪽 폭이
 * 282px 이상이어야 하고, 그 폭은 부모가 보장한다.
 */
import { useRef } from 'react';
import MathName from './MathName';
import { Button, DesktopPagination } from '../common';
import { formatV2Timestamp } from '../../utils/v2DateTime';
// 쪽 이동 부품의 생김새는 이 파일에 있다 — 쓰는 쪽이 함께 들이는 것이 이 저장소 관례다(PaperComposeModal.jsx:54)
import '../../styles/desktop-expanded.css';
import SearchProblemCard from '../ExamPapersV2/SearchProblemCard';
import useSearchBodies from '../ExamPapersV2/useSearchBodies';
import { FIELDS, RANGE_LABEL, isRangeTerm } from './syllabusQuery';
import { groupName } from './quickExpr';
import useResultScroll from './useResultScroll';

/**
 * 결과가 실제로 보낸 식 — 작업 상태 훅은 결과마다 보낸 식의 정규화 JSON 을 key 로 둔다(useSyllabusWorkspace search).
 * 경고의 묶음·항 번호는 **보낸 식** 기준이라, 조건이 바뀐 뒤(stale)에도 그 식으로 읽어야 자리가 맞는다. 못 읽으면 null.
 */
export function searchedExprOf(result) {
  if (typeof result?.key !== 'string') return null;
  try {
    const v = JSON.parse(result.key);
    return v && typeof v === 'object' && Array.isArray(v.groups) ? v : null;
  } catch (_) {
    return null;
  }
}

/** 검색 시각 「HH:MM」 — 한국 시계로. 못 읽으면 빈 글자 */
export function timeText(at) {
  return formatV2Timestamp(at, 'time');
}

/**
 * 서버 경고 [{ group, term, unknown_values }](0부터 센 자리, 회신 §3 R2) → 사람이 읽는 줄.
 * 없는 값의 뜻이 방식마다 다르다 — 「하나라도」는 남은 값으로 찾았고, 「모두·정확히」는 그 항이 늘 거짓이 될 수 있다.
 * 그래서 한 문장으로 뭉뚱그리지 않는다(개편 전 「없는 값 N개는 빼고 셌습니다」는 all/exact 에서 거짓이었다, 설계 §5).
 * 묶음이 하나면 「묶음 A」를 붙이지 않는다 — 빠른 조건도 묶음이 하나일 때는 묶음 이름을 보이지 않는다.
 */
export function warningLines(warnings, expr) {
  const groups = Array.isArray(expr?.groups) ? expr.groups : [];
  const multi = groups.length > 1;
  return (Array.isArray(warnings) ? warnings : []).map((w, i) => {
    const vals = Array.isArray(w?.unknown_values) ? w.unknown_values : [];
    if (!vals.length) return null;
    const g = Number.isInteger(w?.group) ? w.group : null;
    const t = Number.isInteger(w?.term) ? w.term : null;
    const term = g != null && t != null ? groups[g]?.terms?.[t] ?? null : null;
    const label = isRangeTerm(term) ? RANGE_LABEL : FIELDS[term?.field]?.label;
    const cond = label ? `${label} 조건` : (t != null ? `조건 ${t + 1}` : '조건');
    // 항을 식에서 못 찾았으면 묶음 이름이라도 붙여 자리를 짚게 한다
    const where = (multi || !term) && g != null ? `${groupName(g)} · ${cond}` : cond;
    // 「하나라도」는 남은 값으로 찾는다 — 그런데 값이 전부 없는 값이면 남은 것이 없어 그 항은 all/exact 와 똑같이
    // 거짓이다(회신 R2). 그때 「빼고 찾았습니다」라고 하면 0건의 이유를 가린다. 서버가 값을 문자열로 줘도 같게 본다.
    const unknown = new Set(vals.map(String));
    const values = Array.isArray(term?.values) ? term.values : [];
    const noneLeft = values.length > 0 && values.every((v) => unknown.has(String(v)));
    // 「제외」도 남은 값으로 뺀다. 남은 값이 없으면 뺄 것이 없어 그 항은 참이다 — 거르지 않은 것이지 0건의 이유가 아니다(회신 BE-20260915 §1)
    let verdict;
    if ((term?.match === 'any' || term?.match === 'none') && !noneLeft) verdict = `없는 값 ${vals.length}개는 빼고 찾았습니다`;
    else if (term?.match === 'none') verdict = '제외할 값이 모두 없는 값이라 이 조건은 아무 문제도 빼지 않았습니다';
    else if (term?.match === 'any' || term?.match === 'all' || term?.match === 'exact') verdict = '없는 값이 있어 이 조건은 맞는 문제가 없을 수 있습니다';
    else verdict = `없는 값 ${vals.length}개가 있습니다 — 상세 조건에서 확인하세요`;
    return { key: `${g}-${t}-${i}`, text: `${where} — ${verdict}` };
  }).filter(Boolean);
}

const countText = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}문제`;
const errorText = (e) => (typeof e === 'string' ? e : (e?.message || '문제를 검색하지 못했습니다.'));

export default function SearchResults({
  result = null,
  freshness = 'none',
  canSearch = true,
  blockedReason = '',
  onSearch,
  onPage,
  dict = null,
  basisText = '',
  query = null,
  scrollMemory = null,
  scrollKey = null,
  // 두 칸 배치의 오른쪽 칸 — 목록의 스크롤바 자리를 늘 잡아 카드 폭이 스크롤 유무에 흔들리지 않게(문제 v2 보기 칸과 같은 방식)
  gutter = false,
  // 작업 칸이 0.8초 자동 검색을 기다리는 중 — 곧 바뀔 결과라 굵은 「다시 검색하세요」 띠 대신 한 줄로 알린다(설계 FE-20260913 §5)
  autoPending = false,
}) {
  const data = result?.data || null;
  const items = data?.items; // 훅이 쥔 응답 배열 그대로 — 참조가 같으면 본문을 다시 변환하지 않는다
  const { bodies } = useSearchBodies(items);
  const scroll = useResultScroll({ memory: scrollMemory, rowKey: scrollKey, result, bodies });

  const loading = result?.status === 'loading';
  const failed = result?.status === 'error';
  const stale = freshness === 'stale';
  const hasData = data != null;
  const list = Array.isArray(items) ? items : [];
  const total = Number(data?.totalItems ?? list.length) || 0;
  const page = Number(data?.page ?? result?.page ?? 1) || 1;
  const totalPages = Number(data?.totalPages ?? 1) || 1;

  // 쪽 이동은 지금 식이 곧 결과의 식일 때만. DesktopPagination 의 Enter 는 100ms 뒤에 부르므로
  // 그 사이 막힘이 바뀌어도 부르는 순간의 값을 보게 ref 로 읽는다.
  // 보고 있는 쪽도 거른다 — 부품의 단추는 같은 쪽을 거르지만 입력칸 Enter 는 거르지 않아, 5초 예산의 검색을 한 번 더 쓴다.
  const pageBlocked = loading || stale;
  const blockedRef = useRef(pageBlocked);
  blockedRef.current = pageBlocked;
  const pageRef = useRef(page);
  pageRef.current = page;
  const goPage = (p) => {
    if (blockedRef.current || p === pageRef.current) return;
    if (onPage) onPage(p);
  };

  /**
   * [다시 시도] — 같은 식의 쪽 이동이 실패했으면 그 쪽을, 아니면 첫 쪽부터 다시 찾는다.
   * 식이 바뀐 뒤(stale)에는 실패한 쪽 번호가 지금 식과 무관하므로 첫 쪽이다. errorPage 는 실패 요청의 쪽이다.
   */
  const retry = () => {
    const p = Number(result?.errorPage);
    if (!stale && result?.errorKey != null && result.errorKey === result.key && p > 1 && onPage) onPage(p);
    else if (onSearch) onSearch();
  };

  const stamp = timeText(result?.at);
  let headline = '';
  let meta = '';
  if (loading) {
    headline = '검색 중…';
    if (hasData) meta = freshness === 'fresh' ? countText(total) : `이전 검색 · ${countText(total)}`;
  } else if (hasData) {
    const previous = stale || failed;
    headline = previous ? `이전 검색 · ${countText(total)}` : countText(total);
    meta = [previous ? '마지막 검색 기준' : '현재 검색 기준', basisText, stamp].filter(Boolean).join(' · ');
  }

  const warnLines = hasData ? warningLines(data?.warnings, searchedExprOf(result) ?? query) : [];

  let body;
  if (loading && !hasData) {
    body = (
      <div className="space-y-2" aria-hidden="true" data-testid="search-skeleton">
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
      </div>
    );
  } else if (!hasData) {
    // 실패만 있고 이전 결과가 없으면 위 빨간 띠가 전부다 — 여기에 「검색 전」 문구를 겹치지 않는다
    body = failed ? null : (
      <p className="p-6 text-center text-sm text-gray-500">
        {canSearch ? (autoPending ? '곧 검색합니다' : '조건을 고르면 바로 검색합니다') : (blockedReason || '검색할 수 없는 조건입니다.')}
      </p>
    );
  } else if (list.length === 0) {
    body = total > 0 ? (
      // 쪽 번호가 끝을 넘은 경우 — 그 사이 문제가 줄었다
      <p className="p-6 text-center text-sm text-gray-500">이 쪽에 표시할 문제가 없습니다. 앞 쪽으로 돌아가세요.</p>
    ) : (
      <div className="p-6 text-center">
        <p className="text-sm text-gray-700">{stale || failed ? '마지막 검색에 맞는 문제가 없었습니다' : '이 조건에 맞는 문제가 없습니다'}</p>
        {!(stale || failed) && <p className="mt-1 text-xs text-gray-500">조건을 줄이거나 바꿔 보세요</p>}
      </div>
    );
  } else {
    body = (
      // 읽는 중에는 목록을 지우지 않고 흐리게 둔다 — 비웠다 다시 채우면 읽던 자리를 잃는다
      <ul aria-busy={loading || autoPending || undefined} className={`space-y-1 transition-opacity ${loading || autoPending ? 'opacity-50' : ''}`}>
        {list.map((it, i) => {
          const pid = it?.problem_id ?? it?.id;
          return (
            <li key={pid != null ? String(pid) : `row-${i}`}>
              <SearchProblemCard item={it} bodyHtml={pid != null ? (bodies[String(pid)] ?? null) : null} dict={dict} />
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section aria-label="문제 검색 결과" className="flex h-full min-h-0 flex-col gap-2">
      {failed && (
        <div role="alert" className="flex shrink-0 flex-wrap items-start justify-between gap-2 rounded-2xl bg-red-50 p-3 text-xs text-red-600">
          <div className="min-w-0 flex-1">
            <p>{errorText(result.error)}</p>
          </div>
          <Button size="xs" variant="secondary" onClick={retry} disabled={!canSearch}>다시 시도</Button>
        </div>
      )}
      {/* 실패 띠가 있으면 겹치지 않는다 — 둘 다 「다시 찾으라」는 말이다 */}
      {stale && autoPending && !loading && !failed && (
        <p className="shrink-0 text-xs text-gray-500">조건이 바뀌어 곧 다시 검색합니다 — 아래는 마지막 검색의 결과입니다.</p>
      )}
      {stale && !autoPending && !loading && !failed && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <div className="min-w-0 flex-1">
            <p>조건이 바뀌었습니다. 아래는 마지막 검색의 결과입니다. 현재 조건으로 다시 검색하세요.</p>
          </div>
          <Button size="xs" variant="ghost" onClick={() => onSearch && onSearch()} disabled={!canSearch}>다시 검색</Button>
        </div>
      )}

      {/* 머리 글자는 라이브 영역이다 — 노드를 늘 두어야 바뀐 글자를 읽어 준다(검색 상태만 바뀌고, 입력마다 바뀌지 않는다) */}
      <div className="flex shrink-0 flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
        <p role="status" aria-live="polite" className="text-sm font-bold text-gray-900"><MathName text={headline} compact /></p>
        {meta && <p className="text-[11px] text-gray-500">{meta}</p>}
      </div>

      {warnLines.length > 0 && (
        <ul aria-label="검색 경고" className="shrink-0 space-y-0.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {warnLines.map((w) => <li key={w.key}>{w.text}</li>)}
        </ul>
      )}

      {/* 행 복귀 위치는 scroll 훅이 복원한다. 뒤늦은 본문 높이 변화로 브라우저가 위치를 다시 밀지 않게 한다. */}
      <div {...scroll} data-search-scroll="" style={{ overflowAnchor: 'none' }} className={`min-h-0 flex-1 overflow-y-auto${gutter ? ' [scrollbar-gutter:stable]' : ''}`}>{body}</div>

      <div className="flex min-h-8 shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-2">
        <p className="text-[11px] text-gray-500">검색 결과는 저장과 상관없이 지금 조건으로 찾은 것입니다.</p>
        {hasData && totalPages > 1 && (pageBlocked ? (
          // 막힌 동안에는 누를 수 없는 단추를 세우지 않는다 — 누르면 아무 일도 없는 단추는 고장처럼 보인다
          <span className="text-[11px] text-gray-500">{page} / {totalPages}쪽</span>
        ) : (
          <DesktopPagination compact currentPage={page} totalPages={totalPages} onPageChange={goPage} />
        ))}
      </div>
    </section>
  );
}
